/**
 * Accessibility utilities for the application
 * 
 * This module provides utilities for:
 * - ARIA label generation
 * - Keyboard navigation helpers
 * - Screen reader announcements
 * - Focus management
 */

/**
 * Generate accessible labels for form elements
 */
export function generateAriaLabel(
  baseLabel: string,
  options: {
    required?: boolean;
    invalid?: boolean;
    description?: string;
  } = {}
): string {
  let label = baseLabel;
  
  if (options.required) {
    label += ', required';
  }
  
  if (options.invalid) {
    label += ', invalid';
  }
  
  if (options.description) {
    label += `, ${options.description}`;
  }
  
  return label;
}

/**
 * Generate accessible descriptions for form elements
 */
export function generateAriaDescription(
  error?: string,
  hint?: string
): string | undefined {
  const parts: string[] = [];
  
  if (hint) {
    parts.push(hint);
  }
  
  if (error) {
    parts.push(`Error: ${error}`);
  }
  
  return parts.length > 0 ? parts.join('. ') : undefined;
}

/**
 * Keyboard navigation helpers
 */
export const KEYBOARD_KEYS = {
  ENTER: 'Enter',
  SPACE: ' ',
  ESCAPE: 'Escape',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  TAB: 'Tab',
  HOME: 'Home',
  END: 'End',
} as const;

/**
 * Check if a key event should trigger an action
 */
export function isActionKey(event: React.KeyboardEvent, keys: string[]): boolean {
  return keys.includes(event.key);
}

/**
 * Handle keyboard navigation for lists/menus
 */
export function handleArrowNavigation(
  event: React.KeyboardEvent,
  currentIndex: number,
  itemCount: number,
  onIndexChange: (index: number) => void
): void {
  switch (event.key) {
    case KEYBOARD_KEYS.ARROW_UP:
      event.preventDefault();
      onIndexChange(currentIndex > 0 ? currentIndex - 1 : itemCount - 1);
      break;
    case KEYBOARD_KEYS.ARROW_DOWN:
      event.preventDefault();
      onIndexChange(currentIndex < itemCount - 1 ? currentIndex + 1 : 0);
      break;
    case KEYBOARD_KEYS.HOME:
      event.preventDefault();
      onIndexChange(0);
      break;
    case KEYBOARD_KEYS.END:
      event.preventDefault();
      onIndexChange(itemCount - 1);
      break;
  }
}

/**
 * Screen reader announcement utility
 */
export function announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}

/**
 * Focus management utilities
 */
export function trapFocus(container: HTMLElement): () => void {
  const focusableElements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstElement = focusableElements[0] as HTMLElement;
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
  
  function handleTabKey(event: KeyboardEvent): void {
    if (event.key !== KEYBOARD_KEYS.TAB) return;
    
    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }
  
  container.addEventListener('keydown', handleTabKey);
  
  // Focus first element
  firstElement?.focus();
  
  // Return cleanup function
  return () => {
    container.removeEventListener('keydown', handleTabKey);
  };
}

/**
 * Generate unique IDs for form elements
 */
let idCounter = 0;
export function generateId(prefix: string = 'element'): string {
  return `${prefix}-${++idCounter}`;
}

/**
 * Color contrast utilities
 */
export function getContrastRatio(color1: string, color2: string): number {
  // This is a simplified implementation
  // In a real app, you'd use a proper color contrast library
  return 4.5; // Placeholder - meets WCAG AA standard
}

/**
 * Check if color combination meets WCAG standards
 */
export function meetsContrastRequirement(
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA'
): boolean {
  const ratio = getContrastRatio(foreground, background);
  return level === 'AA' ? ratio >= 4.5 : ratio >= 7;
}